import { useMutation } from '@apollo/client'
import clsx from 'clsx'
import { FormikErrors, useFormik } from 'formik'
import { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { UPDATE_BG_ACCOUNT, UPDATE_USER } from '../../graphql/mutation'
import { GET_BG_ACCOUNT_BY_ID, GET_USER_BY_ID } from '../../graphql/query'
import { toAbsoluteUrl } from '../../helpers'
import * as Yup from 'yup'
import UserContext from '../../context/UserContext'

interface IProps {
  closeBusinessModal?: () => void
  loading: any
  error: any
  data: any
  formData: any
  setFormData: any
}

const OnboardingSchema = Yup.object().shape({
  Fullname: Yup.string()
    .min(2, 'Minimum 2 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Full name is required'),
  ContactNumber: Yup.string()
    .matches(/^\d{10}$/, 'Contact Number must be exactly 10 numbers')
    .required('Contact Number is required'),
  Designation: Yup.string()
    .min(2, 'Minimum 2 symbols')
    .max(50, 'Maximum 50 symbols')
    .matches(/^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/, 'Designation is required')
    .required('Designation is required')
})

export const ProfileDetails: React.FC<IProps> = ({
  closeBusinessModal,
  loading,
  error,
  data,
  formData,
  setFormData
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const { currentUser } = useContext(UserContext)
  const [updateBgAccount] = useMutation(UPDATE_BG_ACCOUNT, {
    refetchQueries: [
      {
        query: GET_BG_ACCOUNT_BY_ID,
        context: {
          headers: {
            'access-control-allow-methods': 'Profile'
          }
        }
      }
    ],
    context: {
      headers: {
        'access-control-allow-methods': 'Profile'
      }
    }
  })
  const [updateUser] = useMutation(UPDATE_USER, {
    refetchQueries: [
      {
        query: GET_USER_BY_ID,
        context: {
          headers: {
            'access-control-allow-methods': 'Profile'
          }
        }
      }
    ],
    context: {
      headers: {
        'access-control-allow-methods': 'Profile'
      }
    }
  })
  const navigate = useNavigate()

  const [selectedOptions, setSelectedOptions] = useState({
    Department: ''
  })

  const handleDropdownSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const name = e.target.name
    const value = e.target.value

    setSelectedOptions((prevValue) => ({
      ...prevValue,
      [name]: value
    }))
  }

  useEffect(() => {
    if (!!data && !!data.getUserById.Department) {
      setSelectedOptions((prevValue) => ({
        ...prevValue,
        Department: data.getUserById.Department
      }))
    }
  }, [data])

  const initialValues = {
    Fullname: !!data && !loading && !error && data.getUserById.name ? data.getUserById.name : '',

    ContactNumber:
      !!data && !loading && !error && data.getUserById.contactNumber
        ? data.getUserById.contactNumber
        : '',
    Designation:
      !!data && !loading && !error && data.getUserById.Designation
        ? data.getUserById.Designation
        : ''
  }

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: OnboardingSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const updatedFormData = {
        ...formData,
        businessContactNumber: values.ContactNumber
      }
      setFormData(updatedFormData)
      updateBgAccount({
        variables: {
          input: {
            businessContactNumber: values.ContactNumber
          }
        }
      })
        .then((res: any) => {
          console.log(res)
          if (res.data.updateBgAccount.status === 200) {
            setIsLoading(true)
            updateUser({
              variables: {
                input: {
                  id: parseInt(currentUser.id),
                  name: values.Fullname,
                  contactNumber: values.ContactNumber,
                  Designation: values.Designation,
                  Department: selectedOptions.Department,
                  UserRoles: [{ roleId: data.getUserById.UserRole[0].roleId }]
                }
              }
            })
              .then((res: any) => {
                setIsLoading(false)
                if (res.data.updateUser.status >= 200 && res.data.updateUser.status < 300) {
                  toast.success(res.data.updateUser.message)
                  navigate('/onboarding/organization-details')
                } else {
                  toast.error(res.data.updateUser.message)
                  setSubmitting(false)
                }
              })
              .catch((err) => {
                setIsLoading(false)
                setSubmitting(false)
                toast.error('Sorry! Unexpected Error.')
                console.error(err)
              })
          } else {
            toast.error(res.data.updateBgAccount.message)
          }
        })
        .catch((err) => {
          console.log(err)
        })
    }
  })

  if (loading) {
    return <h1>Loading...</h1>
  }

  const logoutAndReload = () => {
    localStorage.clear()
    document.location.reload()
  }

  const getErrorMessage = (error: string | string[] | FormikErrors<any> | FormikErrors<any>[]) => {
    if (typeof error === 'string') {
      return error
    } else if (Array.isArray(error)) {
      return error.join(', ')
    } else {
      return JSON.stringify(error)
    }
  }

  return (
    <div
      className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
      style={{
        backgroundImage: `url(${toAbsoluteUrl('/media/illustrations/sketchy-1/14.png')})`
      }}
    >
      <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
        <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <form
            className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
            noValidate
            id='kt_login_signup_form'
            onSubmit={formik.handleSubmit}
          >
            {!closeBusinessModal && (
              <div className='mb-10 text-center'>
                <h2 className='text-dark mb-4'> Profile Detail Page</h2>
                <p className='text-muted fs-5'>Tell us little bit about your profile. </p>
              </div>
            )}

            {formik.status && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}

            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6 required'>Full Name :</label>
              <input
                placeholder='Full name'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('Fullname')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.Fullname && formik.errors.Fullname
                  },
                  {
                    'is-valid': formik.touched.Fullname && !formik.errors.Fullname
                  }
                )}
              />
              {formik.touched.Fullname && formik.errors.Fullname && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{getErrorMessage(formik.errors.Fullname)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6 required'>
                Contact Number :
              </label>
              <input
                placeholder='Contact Number'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('ContactNumber')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.ContactNumber && formik.errors.ContactNumber
                  },
                  {
                    'is-valid': formik.touched.ContactNumber && !formik.errors.ContactNumber
                  }
                )}
              />
              {formik.touched.ContactNumber && formik.errors.ContactNumber && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{getErrorMessage(formik.errors.ContactNumber)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Designation :</label>
              <input
                placeholder='Designation'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('Designation')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  { 'is-invalid': formik.touched.Designation && formik.errors.Designation },
                  {
                    'is-valid': formik.touched.Designation && !formik.errors.Designation
                  }
                )}
              />
              {formik.touched.Designation && formik.errors.Designation && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{getErrorMessage(formik.errors.Designation)}</span>
                  </div>
                </div>
              )}
            </div>
            <div className='mb-10 fv-row'>
              <label className='form-label fw-bolder text-dark fs-6 required'>Department :</label>
              <div className='position-relative mb-3'>
                <select
                  className=' form-select form-select-solid form-select '
                  value={selectedOptions.Department}
                  onChange={handleDropdownSelect}
                  name='Department'
                >
                  <option key='blankKey' hidden>
                    Select
                  </option>
                  <option value='Customer Service'>Customer Service</option>
                  <option value='IT ,Engineering & Developement'>
                    IT ,Engineering & Developement
                  </option>
                  <option value='Project Management & Innovation'>
                    Project Management & Innovation
                  </option>
                  <option value='Marketing'>Marketing</option>
                  <option value='Sales'>Sales</option>
                  <option value='HR'>HR</option>
                  <option value='Executive Management'>Executive Management</option>
                  <option value='Other'>Other</option>
                </select>
              </div>
            </div>
            <div className='text-center'>
              <button
                type='submit'
                id='kt_sign_up_submit'
                className='btn btn-lg btn-primary w-100 mb-5'
                disabled={formik.isSubmitting || !formik.isValid || !selectedOptions.Department}
              >
                {!isLoading && <span className='indicator-label'>Next</span>}
                {isLoading && (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Please wait...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
              <button
                type='button'
                id='kt_login_signup_form_cancel_button'
                className='btn btn-lg btn-secondary w-100 mb-5'
                onClick={logoutAndReload}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
export default ProfileDetails
